import type { HierarcicalObjectReference, SearchPattern } from "@novorender/data-js-api";

import type { AppObjectDB } from "./appObjectDb";
import { getFilePathFromObjectPath } from "./objectData";
import { SelectionSet } from "./selectionSet";

/** Extension-less federated sheets stamped by the drawings pipeline. */
const DRAWINGS_SOURCE_PATTERN: SearchPattern = {
    property: "Procore/DocumentSource",
    value: "drawings",
    exact: true,
};

/** Sheets whose path still carries a `.pdf` extension (Contains, not Equal). */
const PDF_PATH_PATTERN: SearchPattern = { property: "path", value: ".pdf" };

/**
 * PDF/drawing sheet ids for the current scene.
 *
 * Drawings are leaves (no descendants, no folder nesting in Procore), so two
 * scene-wide searches are enough: one on `Procore/DocumentSource`, one on path
 * for `.pdf`. They run in parallel and are merged; consumers never observe a
 * partial set.
 *
 * Each search matches on its own terms, so hits are kept only when the path also satisfies
 * the rule `isPdfOrDrawingObjectId` (`dataAccess/utils`) applies: the set gates 3D
 * selection, and an id in it is never read again.
 */
export async function discoverPdfObjectIds(db: AppObjectDB, abortSignal?: AbortSignal): Promise<SelectionSet> {
    abortSignal?.throwIfAborted();
    const [fromSource, fromPath] = await Promise.all([
        collectMatchingIds(db, DRAWINGS_SOURCE_PATTERN, pathAllowsDocument, abortSignal),
        collectMatchingIds(db, PDF_PATH_PATTERN, hasPdfFilePath, abortSignal),
    ]);
    abortSignal?.throwIfAborted();
    return fromSource.withAdded(fromPath);
}

/** The file the path names, or `null` when it names none — the extension-less federated case. */
function filePathOf(object: HierarcicalObjectReference): string | null {
    return object.path ? getFilePathFromObjectPath(object.path) : null;
}

/**
 * `DocumentSource` only decides for paths that name no file, so a hit whose path names a
 * non-PDF one is not a document.
 */
function pathAllowsDocument(object: HierarcicalObjectReference): boolean {
    const filePath = filePathOf(object);
    return filePath === null || filePath.toLowerCase().endsWith(".pdf");
}

/** The `.pdf` is the file itself, not a substring of some folder along the way. */
function hasPdfFilePath(object: HierarcicalObjectReference): boolean {
    return filePathOf(object)?.toLowerCase().endsWith(".pdf") ?? false;
}

async function collectMatchingIds(
    db: AppObjectDB,
    searchPattern: SearchPattern,
    filter: (object: HierarcicalObjectReference) => boolean,
    abortSignal: AbortSignal | undefined,
): Promise<SelectionSet> {
    const ids = new Set<number>();
    const iterator = db.search({ searchPattern: [searchPattern] }, abortSignal);
    for await (const hit of iterator as AsyncIterable<HierarcicalObjectReference>) {
        abortSignal?.throwIfAborted();
        if (filter(hit)) {
            ids.add(hit.id);
        }
    }
    return SelectionSet.fromIds(ids);
}
